import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import React, { ComponentType, useContext } from 'react';
import { useAuth } from 'react-oidc-context';

import {
  UpdateGameDocumentAsync,
  CleanMapIllustrationArea,
  PublishGameDocumentAsync,
  SubmitToGlobalLibraryGameDocumentAsync,
  EnsureUniqueStepIds
} from '../../utils/game-document';
import {
  GetGameDocumentLatestAsync,
  GetGameDocumentContentAsync
} from '../../services/json-document';
import { useGameExport } from '../../hooks/use-game-export';
import { appStore } from '../../stores/app-store';
import PageToolbar from '../../components/page-toolbar';
import HelpSupport from '../../components/help-support';
import { useToast } from '../../contexts/toast-context';
import { LayoutContext } from '../../contexts/layout-provider';
import { saveGameToDB } from '../../utils/game-document/indexedDB';
import { GameDocumentContext } from '../../contexts/game-document';
import SubmitConfirmation from '../../components/submit-to-global-library';
import { useInCompleteEvents } from '../../hooks/use-incomplete-events';
import SaveConflictDialog from '../../components/dialogs/save-conflict-dialog';
import semver from 'semver';

interface ToolbarProps {
  children?: React.ReactNode;
  showDefaultContent?: boolean;
  title: string;
  notificationTitle?: string;
  buttonHelpSupport?: ButtonHelpSupport;
}

interface ButtonHelpSupport {
  title: string;
  url: string;
}

const Toolbar: ComponentType<ToolbarProps> = ({
  showDefaultContent = true,
  notificationTitle,
  ...props
}: ToolbarProps) => {
  const layout = useContext(LayoutContext);
  const [state, setState] = useContext(GameDocumentContext);
  const { onToastOpen } = useToast();
  const auth = useAuth();
  const userRoles = (auth.user?.profile.role as string[]) || [];
  const hasExportPermission = userRoles.includes('ExportGames');
  const {
    taskEvents,
    taskContents,
    zoneEvents,
    worldMapEvents,
    timerEvents,
    areaEvents
  } = useInCompleteEvents();

  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [showConflictDialog, setShowConflictDialog] =
    React.useState<boolean>(false);
  const { triggerExport, renderExportProgressDialog } = useGameExport();
  const [gameStatus, setGameStatus] = React.useState<
    string | 'New || Pending || Approved || Rejected || Published'
  >('');

  const toggleDialog = () => {
    setShowDialog(!showDialog);
  };

  const getLatestDocument = async () => {
    if (state.gameId) {
      try {
        const latest = await GetGameDocumentLatestAsync(
          parseInt(state.gameId.toString())
        );

        if (latest) {
          setGameStatus(latest.status as string);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  React.useEffect(() => {
    getLatestDocument();
  }, [state.gameId, state.gameDocument?.version]);

  const checkForConflicts = async (): Promise<boolean> => {
    try {
      const serverMetadata = await GetGameDocumentLatestAsync(state.gameId!);
      if (!serverMetadata || !state.gameDocument) return false;

      const serverVersion = serverMetadata.version || '0.0.0';
      const localVersion = state.gameDocument.version || '0.0.0';

      const serverDate = serverMetadata.modifiedDateUtc
        ? new Date(serverMetadata.modifiedDateUtc).getTime()
        : 0;
      const localDate = state.modifiedDateUtc
        ? new Date(state.modifiedDateUtc).getTime()
        : 0;

      if (serverDate > localDate || semver.gt(serverVersion, localVersion)) {
        setShowConflictDialog(true);
        return true; // Conflict found
      }
      return false;
    } catch (error) {
      console.error('Error checking for conflicts:', error);
      return false;
    }
  };

  const onSaveDraft = async () => {
    try {
      appStore.showLoading();
      if (!state.isValid) {
        throw Error();
      }

      if (await checkForConflicts()) {
        return;
      }

      let cleanedGameDocument = CleanMapIllustrationArea(state.gameDocument!);
      cleanedGameDocument = EnsureUniqueStepIds(cleanedGameDocument);

      const {
        assets: { tasks, taskContents: contents, timers, areas }
      } = cleanedGameDocument;

      const isValidTaskContents = taskContents(contents ?? []);
      if (!isValidTaskContents) return;

      const isValidEvents = taskEvents(tasks ?? []);
      if (!isValidEvents) return;

      const isValidTimerEvents = timerEvents(timers ?? []);
      if (!isValidTimerEvents) return;

      const isValidAreaEvents = areaEvents(areas ?? []);
      if (!isValidAreaEvents) return;

      const isValidZoneEvents = zoneEvents();
      if (!isValidZoneEvents) return;

      const isValidWorldMapEvents = worldMapEvents();
      if (!isValidWorldMapEvents) return;

      const { gameDocument: updatedGameDocument, modifiedDateUtc } =
        await UpdateGameDocumentAsync(state.gameId!, cleanedGameDocument);

      const content = {
        ...state,
        isDirty: false,
        modifiedDateUtc: modifiedDateUtc
          ? new Date(modifiedDateUtc).toISOString()
          : state.modifiedDateUtc,
        gameDocument: updatedGameDocument
      };

      setState(content);
      saveGameToDB(content);

      onToastOpen({
        style: 'success',
        body: (
          <div>
            Draft {notificationTitle ? notificationTitle : 'game'} saved.
          </div>
        ),
        title: `${notificationTitle !== undefined ? notificationTitle : ''}`
      });
    } catch (err) {
      onToastOpen({
        style: 'error',
        title: `${notificationTitle !== undefined ? notificationTitle : ''}`,
        body: (
          <div>
            Draft {notificationTitle ? notificationTitle : 'game'} failed to
            save. Please try again.
          </div>
        )
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onReload = async () => {
    try {
      appStore.showLoading();
      let documentLatestResponse = await GetGameDocumentLatestAsync(
        state.gameId!
      );
      if (documentLatestResponse) {
        const content = await GetGameDocumentContentAsync(
          state.gameId!,
          documentLatestResponse.id!
        );

        const updatedGameDocument = {
          ...state,
          isLoaded: true,
          modifiedDateUtc: documentLatestResponse.modifiedDateUtc
            ? new Date(documentLatestResponse.modifiedDateUtc).toISOString()
            : state.modifiedDateUtc,
          gameDocument: content
        };

        setState(updatedGameDocument);
        saveGameToDB(updatedGameDocument);
      }
    } catch (err) {
      onToastOpen({
        style: 'error',
        title: `${notificationTitle !== undefined ? notificationTitle : ''}`,
        body: (
          <div>
            {notificationTitle ? notificationTitle : 'Game'} failed to load.
            Please try again.
          </div>
        )
      });
    } finally {
      appStore.hideLoading();
      setShowConflictDialog(false);
      setShowDialog(false);
    }
  };

  const onPublish = async () => {
    try {
      appStore.showLoading();
      if (!state.isValid) {
        throw Error();
      }

      if (await checkForConflicts()) {
        return;
      }
      const {
        assets: { tasks, taskContents: contents, areas, timers }
      } = state.gameDocument!;

      const isValidTaskContents = taskContents(contents ?? [], 'publish');
      if (!isValidTaskContents) return;

      const isValidEvents = taskEvents(tasks ?? [], 'publish');
      if (!isValidEvents) return;

      const isValidTimerEvents = timerEvents(timers ?? []);
      if (!isValidTimerEvents) return;

      const isValidAreaEvents = areaEvents(areas ?? []);
      if (!isValidAreaEvents) return;

      const isValidZoneEvents = zoneEvents('publish');
      if (!isValidZoneEvents) return;

      const isValidWorldMapEvents = worldMapEvents('publish');
      if (!isValidWorldMapEvents) return;

      const { gameDocument: updatedGameDocument, modifiedDateUtc } =
        await PublishGameDocumentAsync(state.gameId!, state.gameDocument!);

      const content = {
        ...state,
        isDirty: false,
        modifiedDateUtc: modifiedDateUtc
          ? new Date(modifiedDateUtc).toISOString()
          : state.modifiedDateUtc,
        gameDocument: updatedGameDocument
      };

      setState(content);
      saveGameToDB(content);

      onToastOpen({
        style: 'success',
        title: 'Game overview',
        body: <div>Game published.</div>
      });
    } catch (err) {
      onToastOpen({
        style: 'error',
        title: 'Game overview',
        body: <div>Game failed to publish. Please try again.</div>
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onSubmitToGlobal = async () => {
    try {
      appStore.showLoading();
      if (!state.isValid) {
        throw Error();
      }

      if (await checkForConflicts()) {
        return;
      }

      const { gameDocument: updatedGameDocument, modifiedDateUtc } =
        await SubmitToGlobalLibraryGameDocumentAsync(
          state.gameId!,
          state.gameDocument!
        );

      const content = {
        ...state,
        isDirty: false,
        modifiedDateUtc: modifiedDateUtc
          ? new Date(modifiedDateUtc).toISOString()
          : state.modifiedDateUtc,
        gameDocument: updatedGameDocument
      };

      setState(content);
      saveGameToDB(content);

      onToastOpen({
        title: 'Game overview',
        body: <div>Game submitted and waiting for approval.</div>,
        style: 'success'
      });
    } catch (err) {
      onToastOpen({
        title: 'Game overview',
        body: <div>Game failed to submit. Please try again.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      toggleDialog();
    }
  };

  const onExport = async () => {
    if (!state.gameId) {
      onToastOpen({
        style: 'error',
        title: 'Export Game',
        body: <div>Game ID is not available.</div>
      });
      return;
    }
    const gameTitle = state.gameDocument?.name || `game_${state.gameId}`;
    triggerExport(state.gameId, gameTitle);
  };

  return (
    <>
      <PageToolbar
        {...(!layout.isMobile && {
          title: props.title
        })}>
        <div
          className={`d-flex justify-content-${layout.isMobile ? 'between' : 'end'} w-100`}>
          {showDefaultContent && (
            <>
              <div className={'d-flex align-items-center'}>
                <code className={'me-1'}>v{state.gameDocument?.version}</code>
                <Button
                  onClick={onReload}
                  themeColor={'info'}
                  className={'me-1'}
                  fillMode={'flat'}>
                  <span className={'material-symbols-outlined'}>sync</span>
                </Button>
              </div>
              <div className={'d-flex align-items-center'}>
                {props.buttonHelpSupport && (
                  <span className="me-2">
                    <HelpSupport
                      title={props.buttonHelpSupport.title}
                      url={props.buttonHelpSupport.url}
                    />
                  </span>
                )}
                <Button
                  onClick={onSaveDraft}
                  disabled={!state.isDirty}
                  themeColor={'secondary'}
                  className={'me-1'}>
                  Save
                </Button>
                <Button
                  onClick={onPublish}
                  themeColor={'warning'}
                  disabled={['', 'Pending'].includes(gameStatus)}
                  className={'me-1'}>
                  Publish
                </Button>
                {hasExportPermission && (
                  <Button
                    onClick={onExport}
                    themeColor={'primary'}
                    className={'me-1'}>
                    Export
                  </Button>
                )}
                {props.children}
              </div>
            </>
          )}
          {!showDefaultContent && (
            <div className={'d-flex align-items-center'}>{props.children}</div>
          )}
        </div>
      </PageToolbar>

      {showDialog && (
        <Dialog
          width={'50vw'}
          title={
            <span className="fw-bold text-primary">
              Submit to global library
            </span>
          }
          onClose={toggleDialog}>
          <SubmitConfirmation
            toggleDialog={toggleDialog}
            isPendingApproval={gameStatus === 'Pending'}
            onSubmit={onSubmitToGlobal}
          />
        </Dialog>
      )}
      {showConflictDialog && (
        <SaveConflictDialog
          onReload={onReload}
          onCancel={() => setShowConflictDialog(false)}
        />
      )}
      {renderExportProgressDialog()}
    </>
  );
};

export default Toolbar;
